import React, { useEffect, useState } from 'react';
import Sidebar from './AppShell/Sidebar';
import Topbar from './AppShell/Topbar';
import Overview from './pages/Overview';
import Appointments from './pages/Appointments';
import Team from './pages/Team';
import AdminCustomerPortal from './pages/AdminCustomerPortal';
import CustomerPortal from './pages/CustomerPortal';
import Settings from './pages/Settings';

// Non-destructive new dashboard scaffold (v2)
// - Session/role-aware
// - Tabs rendered based on role
// - API integration placeholders using existing PHP endpoints under public/api/

const TABS = {
  overview: { key: 'overview', label: 'Overview' },
  appointments: { key: 'appointments', label: 'Appointments' },
  team: { key: 'team', label: 'Team', adminOnly: true },
  adminPortal: { key: 'adminPortal', label: 'Admin Customer Portal', adminOnly: true },
  customerPortal: { key: 'customerPortal', label: 'Customer Portal' },
  settings: { key: 'settings', label: 'Settings' },
};

export default function DashboardV2() {
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    async function checkAuth() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch('/api/check-auth.php', {
          credentials: 'include',
          signal: controller.signal,
        });
        const data = await res.json();
        if (!data?.success || !data?.user) {
          setUser(null);
          setError('Not authenticated. Please log in.');
        } else {
          setUser(data.user);
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError('Failed to verify session.');
      } finally {
        setLoading(false);
      }
    }
    checkAuth();
    return () => controller.abort();
  }, []);

  const role = user?.role || 'user';

  const visibleTabs = Object.values(TABS).filter(
    (t) => !(t.adminOnly && role !== 'admin')
  );

  function renderTab() {
    switch (activeTab) {
      case 'overview':
        return <Overview role={role} />;
      case 'appointments':
        return <Appointments role={role} user={user} />;
      case 'team':
        return <Team role={role} />;
      case 'adminPortal':
        return <AdminCustomerPortal role={role} />;
      case 'customerPortal':
        return <CustomerPortal user={user} />;
      case 'settings':
        return <Settings user={user} />;
      default:
        return <Overview role={role} />;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-gray-700">Loading dashboard…</div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="max-w-md w-full p-6 rounded-lg border border-red-200 bg-red-50">
          <h2 className="text-lg font-semibold text-red-700 mb-2">Authentication required</h2>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <a href="/" className="inline-flex items-center px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Topbar user={user} onTabSelect={setActiveTab} />
      <div className="flex">
        <Sidebar
          tabs={visibleTabs}
          activeTab={activeTab}
          onSelect={setActiveTab}
        />
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          {renderTab()}
        </main>
      </div>
    </div>
  );
}
